import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/providers/AuthProvider';
import type { AppError } from '@/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type CheckoutAuthProps = {
  onAuthSuccess: () => void;
};

type RegisterForm = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

type LoginForm = {
  email: string;
  password: string;
};

export default function CheckoutAuth({ onAuthSuccess }: CheckoutAuthProps) {
  const { t } = useTranslation();
  const { login, register: authRegister } = useAuth();
  const [mode, setMode] = useState<'register' | 'login'>('register');
  const [error, setError] = useState<string | null>(null);

  const registerSchema = z.object({
    firstName: z.string().min(1, t('validation.first_name_required')),
    lastName: z.string().min(1, t('validation.last_name_required')),
    email: z.string().email(t('validation.email_invalid')),
    password: z
      .string()
      .min(8, t('validation.password_min'))
      .regex(/[A-Z]/, t('validation.password_uppercase'))
      .regex(/[a-z]/, t('validation.password_lowercase'))
      .regex(/[0-9]/, t('validation.password_number')),
  });

  const loginSchema = z.object({
    email: z.string().email(t('validation.email_invalid')),
    password: z.string().min(1, t('validation.required')),
  });

  const registerForm = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  });

  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const handleRegister = async (data: RegisterForm) => {
    setError(null);
    try {
      await authRegister(data);
      onAuthSuccess();
    } catch (err) {
      const appErr = err as AppError;
      if (appErr.code === 'EMAIL_EXISTS') {
        setError(t('auth.error_email_exists'));
      } else {
        setError(t('auth.error_generic'));
      }
    }
  };

  const handleLogin = async (data: LoginForm) => {
    setError(null);
    try {
      await login(data.email, data.password);
      onAuthSuccess();
    } catch (err) {
      const appErr = err as AppError;
      if (appErr.code === 'INVALID_CREDENTIALS') {
        setError(t('auth.error_invalid_credentials'));
      } else if (appErr.code === 'ACCOUNT_LOCKED') {
        setError(t('auth.error_account_locked'));
      } else {
        setError(t('auth.error_generic'));
      }
    }
  };

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        {mode === 'register' ? (
          <>
            <div className="space-y-1 text-center">
              <h3 className="text-lg font-semibold">
                {t('checkout.create_account_to_continue')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('checkout.already_have_account')}{' '}
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(null); }}
                  className="text-primary hover:underline"
                >
                  {t('checkout.sign_in_here')}
                </button>
              </p>
            </div>

            {error && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <form onSubmit={registerForm.handleSubmit(handleRegister)} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>{t('auth.first_name')}</Label>
                  <Input autoComplete="given-name" {...registerForm.register('firstName')} />
                  {registerForm.formState.errors.firstName && (
                    <p className="text-xs text-destructive">
                      {registerForm.formState.errors.firstName.message}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label>{t('auth.last_name')}</Label>
                  <Input autoComplete="family-name" {...registerForm.register('lastName')} />
                  {registerForm.formState.errors.lastName && (
                    <p className="text-xs text-destructive">
                      {registerForm.formState.errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <Label>{t('auth.email')}</Label>
                <Input type="email" autoComplete="email" {...registerForm.register('email')} />
                {registerForm.formState.errors.email && (
                  <p className="text-xs text-destructive">
                    {registerForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label>{t('auth.password')}</Label>
                <Input type="password" autoComplete="new-password" {...registerForm.register('password')} />
                {registerForm.formState.errors.password && (
                  <p className="text-xs text-destructive">
                    {registerForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={registerForm.formState.isSubmitting}
              >
                {registerForm.formState.isSubmitting
                  ? t('common.loading')
                  : t('checkout.sign_up_and_book')}
              </Button>
            </form>
          </>
        ) : (
          <>
            <div className="space-y-1 text-center">
              <h3 className="text-lg font-semibold">
                {t('checkout.sign_in_to_continue')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('checkout.no_account')}{' '}
                <button
                  type="button"
                  onClick={() => { setMode('register'); setError(null); }}
                  className="text-primary hover:underline"
                >
                  {t('checkout.create_one')}
                </button>
              </p>
            </div>

            {error && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-3">
              <div className="space-y-1">
                <Label>{t('auth.email')}</Label>
                <Input type="email" autoComplete="email" {...loginForm.register('email')} />
                {loginForm.formState.errors.email && (
                  <p className="text-xs text-destructive">
                    {loginForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label>{t('auth.password')}</Label>
                <Input type="password" autoComplete="current-password" {...loginForm.register('password')} />
                {loginForm.formState.errors.password && (
                  <p className="text-xs text-destructive">
                    {loginForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loginForm.formState.isSubmitting}
              >
                {loginForm.formState.isSubmitting
                  ? t('common.loading')
                  : t('checkout.sign_in_and_book')}
              </Button>

              <div className="text-center">
                <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                  {t('auth.forgot_password')}
                </Link>
              </div>
            </form>
          </>
        )}
      </CardContent>
    </Card>
  );
}
